/**
 * Confirmation modal component.
 * Reusable modal for confirmations, deletions, and other actions requiring user confirmation.
 */

import { Button } from '@/components/ui/button';
import { X, AlertTriangle } from 'lucide-react';
import './confirmation-modal.scss';

export interface ConfirmationModalProps {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  showIcon?: boolean;
}

export function ConfirmationModal({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'info',
  onConfirm,
  onCancel,
  isLoading = false,
  showIcon = true,
}: ConfirmationModalProps) {
  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <div className="confirmation-modal" role="dialog" aria-labelledby="confirmation-modal-title" aria-modal="true">
      <div className="confirmation-modal__backdrop" onClick={onCancel} />
      <div className="confirmation-modal__content">
        <div className="confirmation-modal__header">
          <div className="confirmation-modal__header-content">
            {showIcon && (
              <div className={`confirmation-modal__icon confirmation-modal__icon--${variant}`}>
                {variant === 'danger' && <AlertTriangle />}
                {variant === 'warning' && <AlertTriangle />}
                {variant === 'info' && <AlertTriangle />}
              </div>
            )}
            <div>
              <h2 id="confirmation-modal-title" className="h3">
                {title}
              </h2>
            </div>
          </div>
          <button
            className="confirmation-modal__close"
            onClick={onCancel}
            aria-label="Close modal"
            disabled={isLoading}
          >
            <X />
          </button>
        </div>

        <div className="confirmation-modal__body">
          <p className="confirmation-modal__message">{message}</p>
        </div>

        <div className="confirmation-modal__footer">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

